/**
 * Phase 5 PR 5.1 — `useUpcomingRace` hook.
 *
 * Returns the next race for the user, in the existing `Race` shape
 * consumed by `<ChampionshipCard />` / `<ChampionshipCardDesktop />`.
 *
 * Scope: OR semantics — the user's PERSONAL meets OR the team's meets.
 * Mirrors `handleGenerateMacroPlan` at `api/index.js:4641`. This was
 * caught in Phase 5 plan §"Round 4 → 5"; an earlier draft that used
 * `.eq('user_id', me).eq('team_id', season.team_id)` would have missed
 * team meets the athlete wasn't directly tagged on.
 *
 * Query construction (no `team_id.eq.null` — PostgREST handles "IS NULL"
 * differently from `.eq.null`):
 *   - When `season.team_id` is non-null:
 *       .or('user_id.eq.<me>,team_id.eq.<id>').gte('meet_date', today)
 *   - When `season.team_id` is null:
 *       .eq('user_id', '<me>').gte('meet_date', today)
 *
 * Ordering: `meet_date ASC LIMIT 1` — the next race only.
 *
 * Returns `null` when:
 *   - no active season → caller renders `<NoRaceCard />`
 *   - active season but no upcoming meet → `<NoRaceCard />`
 *   - query fails → `<NoRaceCard />` (with `error` populated)
 *
 * 2.0 wave 2 (W2-GOAL-LINE, spec 7.4): with `today_2_0_v1` on, a SECOND select
 * reads the viewer's own `meet_goal_times` row for the race just found
 * (`meet_id, goal_time_seconds`, filtered by the meet id only: RLS returns the
 * viewer's row and nobody else's, so no client `user_id` filter is added as a
 * security measure). It is issued only after the first query returned a race,
 * and a failure of it (table absent, schema cache lag, error) never hides the
 * race: the card renders with no Goal line. Flag off = the one legacy request.
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import type { Race } from '../fixtures/race';
import type { ActiveSeason } from './useActiveSeason';
import { useServerFlag } from './useServerFlag';

/** `Race` plus the viewer's goal in whole seconds (W2-GOAL-LINE, GL-2). The v2 cards
 *  read `goalTimeSeconds`; `goalTime` keeps its legacy value for the legacy card. */
export type UpcomingRace = Race & { goalTimeSeconds: number | null };

export interface UseUpcomingRaceArgs {
  /** Active season — used for OR-scoping and progressPct calc. When
   *  null, the hook returns null without querying. */
  season: ActiveSeason | null;
}

export interface UseUpcomingRaceResult {
  data: UpcomingRace | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface TeamMeetRow {
  id: string;
  team_id: string | null;
  user_id: string | null;
  meet_name: string;
  meet_date: string; // ISO date 'YYYY-MM-DD'
  meet_time: string | null;
  location: string | null;
  course_name: string | null;
  distance: string[] | null;
  priority: number;
  /** The viewer's own goal on this meet, in whole seconds. `team_meets` carries no
   *  such column: the hook fills it from the viewer's `meet_goal_times` row
   *  (W2-GOAL-LINE). `tests/fixtures/meet-row-with-goal.json` is the row shape. */
  goal_time_seconds?: number | null;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function daysBetween(from: Date, to: Date): number {
  const fromMid = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toMid = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toMid.getTime() - fromMid.getTime()) / ONE_DAY_MS);
}

function formatMeetDate(iso: string): string {
  // ISO 'YYYY-MM-DD' parsed as UTC midnight; we format with no TZ shift
  // by treating as a local date.
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  const local = new Date(year, month - 1, day);
  const fmt = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return fmt.format(local);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Compute season-progress percentage for the PhaseTrack on the
 * Championship card. Maps today onto [season.start_date, meet_date]:
 *
 *   0% at season start
 *   100% at meet day
 *   linear in between, clamped
 *
 * Falls back to 0 if start_date is missing or after meet_date (shouldn't
 * happen but defensive).
 */
function computeProgressPct(seasonStart: string | null, meetIso: string, now: Date = new Date()): number {
  if (!seasonStart) return 0;
  const [sy, sm, sd] = seasonStart.split('-').map(Number);
  const [my, mm, md] = meetIso.split('-').map(Number);
  if (!sy || !my) return 0;
  const start = new Date(sy, sm - 1, sd).getTime();
  const meet = new Date(my, mm - 1, md).getTime();
  if (meet <= start) return 0;
  const today = now.getTime();
  const pct = ((today - start) / (meet - start)) * 100;
  return Math.round(clamp(pct, 0, 100));
}

/**
 * Adapt a TeamMeetRow + season into the `Race` shape consumed by the
 * existing ChampionshipCard. PR 5.1 leaves `goalTime`, `currentFit`,
 * `phase` at sensible defaults — Phase 5.2 will wire `phase` from the
 * macro-plan once that hook exists. `motivationalLine` is left
 * undefined (valid ReactNode) so the motivational block renders blank
 * rather than a placeholder.
 *
 * W2-GOAL-LINE (GL-2): also returns `goalTimeSeconds`, the viewer's goal from an
 * optional `goal_time_seconds` on the row (null when absent). `goalTime` keeps
 * its legacy value; the v2 cards print "Goal 19:30" from `goalTimeSeconds`.
 *
 * Pure — exported for unit tests.
 */
export function adaptToRace(
  meet: TeamMeetRow,
  season: ActiveSeason | null,
  now: Date = new Date(),
): UpcomingRace {
  const distance = (meet.distance && meet.distance[0]) || 'Race';
  const daysAway = Math.max(0, daysBetween(now, parseLocalDate(meet.meet_date)));
  const location = meet.location || meet.course_name || '';
  const progressPct = computeProgressPct(season?.start_date ?? null, meet.meet_date, now);

  return {
    name: meet.meet_name,
    distance,
    date: formatMeetDate(meet.meet_date),
    location,
    daysAway,
    goalTime: '—', // PR 5.1: no goalTime contract on team_meets — Phase 6b candidate
    goalTimeSeconds: typeof meet.goal_time_seconds === 'number' ? meet.goal_time_seconds : null,
    currentFit: '',
    phase: 'Build', // PR 5.1: static default — PR 5.2 reads from macro_plan
    progressPct,
    // PR 5.1: motivationalLine is supplied by `useMotivation` which
    // calls `/api/motivation` (LLM-generated, daily-cached on
    // `athlete_context._daily_motivation`). The hook composes the line
    // at the route level so the race adapter stays a pure DB-shape
    // adapter. Hidden when motivation hasn't loaded.
    motivationalLine: undefined as unknown as Race['motivationalLine'],
  };
}

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}

/**
 * Today's date string in 'YYYY-MM-DD' for PostgREST `.gte` predicates.
 * Local-timezone — matches how athletes think about "today's race."
 */
function todayIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The Train pages: `/plan` and everything under it. The router matches case-insensitively and with a trailing slash. */
function isTrainPath(pathname: string | undefined): boolean {
  if (!pathname) return false;
  const path = pathname.replace(/\/+$/, '').toLowerCase();
  return path === '/plan' || path.startsWith('/plan/');
}

type SupabaseClient = NonNullable<ReturnType<typeof useAuthContext>['supabase']>;

/**
 * The viewer's own goal on this race, under RLS, in whole seconds (null when there is none). Its failure (the table
 * absent before its migration reaches this project, a schema cache miss, a network error) only drops the Goal line;
 * it never hides the race (GL-7).
 */
async function readGoalSeconds(supabase: SupabaseClient, meetId: string): Promise<number | null> {
  try {
    const { data: goalRow, error: goalError } = await supabase
      .from('meet_goal_times')
      .select('meet_id, goal_time_seconds')
      .eq('meet_id', meetId)
      .maybeSingle();
    const seconds = (goalRow as { goal_time_seconds?: unknown } | null)?.goal_time_seconds;
    return !goalError && typeof seconds === 'number' ? seconds : null;
  } catch {
    return null;
  }
}

export function useUpcomingRace({ season }: UseUpcomingRaceArgs): UseUpcomingRaceResult {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;
  // W2-GOAL-LINE (GL-7): the goal read rides the 2.0 Today flag. A string literal, read
  // above every early return (hooks never sit below one, React error #310).
  const goalLineOn = useServerFlag('today_2_0_v1');
  // FIX-TRAIN: the goal is printed by Today's card only. Every Train page (`/plan`, `/plan/calendar`, `/plan/season`)
  // shows the race by name and date, so there the hook reads no goal. Read from the router's location context when
  // there is one, so the hook stays usable bare (a missing router reads as "not a Train page").
  const routerLocation = useContext(UNSAFE_LocationContext) as { location: { pathname: string } } | null;
  const goalRead = goalLineOn && !isTrainPath(routerLocation?.location.pathname);

  const [data, setData] = useState<UpcomingRace | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // The flag answering is not a reason to read the race again (FIX-TRAIN): the read below does not depend on it.
  // It looks at the answer when it needs it, here, and a flag that answers after the race is in hand takes the
  // effect further down, which reads the goal alone.
  const goalReadRef = useRef(goalRead);
  goalReadRef.current = goalRead;
  // Which read may write. A read that has been superseded (a new season, a refetch, the season going away) lands
  // after the newer one more often than a test would guess; it must not overwrite it or end its `loading`.
  const readSeq = useRef(0);
  // The race of the last settled read, and the race whose goal has been asked for (so the flag effect asks once).
  const meetRef = useRef<TeamMeetRow | null>(null);
  const goalAskedFor = useRef<string | null>(null);

  const refetch = useCallback(async () => {
    const seq = (readSeq.current += 1);
    meetRef.current = null;
    goalAskedFor.current = null;
    if (!supabase || !userId || !season) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const today = todayIsoDate();
      let query = supabase
        .from('team_meets')
        .select('*')
        .gte('meet_date', today)
        .order('meet_date', { ascending: true })
        .limit(1);

      // OR-scoping. PostgREST .or() syntax uses commas.
      if (season.team_id) {
        query = query.or(`user_id.eq.${userId},team_id.eq.${season.team_id}`);
      } else {
        query = query.eq('user_id', userId);
      }

      const { data: rows, error: queryError } = await query.maybeSingle();
      if (seq !== readSeq.current) return;
      if (queryError) throw queryError;
      const meet = (rows as TeamMeetRow | null) ?? null;
      meetRef.current = meet;
      if (meet && goalReadRef.current) {
        goalAskedFor.current = meet.id;
        const goalSeconds = await readGoalSeconds(supabase, meet.id);
        if (seq !== readSeq.current) return;
        setData(adaptToRace(goalSeconds === null ? meet : { ...meet, goal_time_seconds: goalSeconds }, season));
      } else {
        setData(meet ? adaptToRace(meet, season) : null);
      }
    } catch (err) {
      if (seq !== readSeq.current) return;
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [supabase, userId, season]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // The flag answered (or the page became one that prints the goal) after the race was read: read the goal for the
  // race in hand, and nothing else. Idle until a race is in hand, and once per race.
  useEffect(() => {
    const meet = meetRef.current;
    if (!goalRead || !meet || !supabase || !season || goalAskedFor.current === meet.id) return;
    goalAskedFor.current = meet.id;
    const seq = (readSeq.current += 1);
    void readGoalSeconds(supabase, meet.id).then((goalSeconds) => {
      if (seq !== readSeq.current) return;
      setData(adaptToRace(goalSeconds === null ? meet : { ...meet, goal_time_seconds: goalSeconds }, season));
    });
  }, [goalRead, supabase, season]);

  if (!supabase || !userId) {
    return { data: null, loading: false, error: null, refetch: async () => {} };
  }

  return { data, loading, error, refetch };
}

// Exported for unit-testing the pure helpers without a live client.
export const __testing__ = {
  adaptToRace,
  computeProgressPct,
  daysBetween,
  formatMeetDate,
  todayIsoDate,
};
